import styled from "@emotion/styled";
import { Button, Card, CardContent, CardHeader, Divider, IconButton, useTheme } from "@mui/material";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { BasicFlex } from "../../../utils/Common";

import { capitalize } from "lodash";

import MultiStatusChip from "../../../utils/utilities/MultiStatusChip";

import SimpleChip from "../../../utils/utilities/SimpleChip";

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HouseRoundedIcon from '@mui/icons-material/HouseRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import useSnackbarStore from "../../../store/snackbar";

import { formatCurrency } from "../../../utils/util";

import { markStageComplete } from "../apiService";

const RepairCardStyles = styled(Card)`
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.secondaryFaint};
  }
`;


const StatusChip = ({ repair }) => {
  return (
    <MultiStatusChip value={repair?.status} values={[{value: "scheduled", label: "Scheduled", status: "error"}, {value: "in_progress", label: "Processing", status: "warning"}, {value: "completed", label: "Completed", status: "success"}]} />
  )
}

// First repair (by repair_order) that isn't completed
const getNextRepair = (repairs = []) => {
  return [...repairs]
    .sort((a, b) => a?.repair_order - b?.repair_order)
    .find(r => r?.status !== "completed");
}

const RepairCard = ({ repair, active, handleActiveRepair= () => {}, index, removeRepair, repairs }) => {
  let duration = repair?.in_house ? `${repair?.duration_in_minutes} Min` : `${repair?.estimated_time_to_complete} Days`;

  const theme = useTheme();
  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const { mutate: completeStage, isPending: isCompleting } = useMutation({
    mutationFn: markStageComplete,
    onSuccess: (data) => {
      if (!data) return; // markStageComplete returns null on error
      queryClient.invalidateQueries({ queryKey: ['repair_tickets'] });
      showSnackbar('Updated successfully!', 'success');
    },
    onError: (error) => {
      showSnackbar(error.message, 'error');
    }
  });

  const nextRepair = repairs ? getNextRepair(repairs) : null;
  const canComplete = !!nextRepair && nextRepair.repair_order === repair?.repair_order;

  const handleComplete = (e) => {
    e.stopPropagation();
    completeStage({ repair_task_id: repair?.repair_task_id });
  }

  return (
    <RepairCardStyles variant="outlined" sx={{width: "100%", borderColor: active ? theme.palette.secondary.main : "", backgroundColor: active ? theme.palette.secondary.faint : ""}} onClick={() => handleActiveRepair(repair?.repair_order)}>
      <CardHeader subheader={<div>{repair.repair_order}. &nbsp; {repair?.repair_name}</div>} sx={{p: 1}}
      action={
        <BasicFlex gap="1rem">
          {canComplete && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckCircleRoundedIcon />}
              disabled={isCompleting}
              onClick={handleComplete}
            >
              {isCompleting ? "Completing..." : "Mark Complete"}
            </Button>
          )}
          <StatusChip repair={repair} />
          {removeRepair && <IconButton onClick={() => removeRepair(repair)}><RemoveCircleOutlineRoundedIcon color="error" /></IconButton>}
        </BasicFlex>
      }
      slotProps={{action: {sx: {mr: "0", mt: "-.3rem"}}}}
       />
      <Divider />
      <CardContent sx={{p: 1, pb: "1rem !important"}}>
        <BasicFlex >
          <BasicFlex dir="column" gap=".8rem">
            <BasicFlex justify="flex-start" gap="1rem">
              <SupportAgentRoundedIcon fontSize="small" color="basic" />
              <div><SimpleChip label={repair?.specialist_name || repair?.vendor_name} size="small" uc={true} /></div>
            </BasicFlex>
            <BasicFlex justify="flex-start" gap="1rem">
              <AttachMoneyIcon fontSize="small" color="basic" />
              <div>{formatCurrency(repair?.price)}</div>
            </BasicFlex>
          </BasicFlex>

          <BasicFlex dir="column" gap=".8rem">
            <BasicFlex justify="flex-start" gap="1rem">
              <AccessTimeIcon fontSize="small" color="basic" />
              <div>{duration}</div>
            </BasicFlex>
            <BasicFlex justify="flex-start" gap="1rem">
              <HouseRoundedIcon fontSize="small" color="basic" />
              <div>{repair?.in_house ? "In House" : "External"}</div>
            </BasicFlex>
          </BasicFlex>
        </BasicFlex>
        
      </CardContent>
    </RepairCardStyles>
  );
};

export default RepairCard;